import { useState } from 'react'
import { useAuth } from '@/lib/auth'
import { Brand } from '@/components/Brand'
import { Button } from '@/components/ui/primitives'

/**
 * There is no form here, and there never will be.
 *
 * Codemax ARMS is the only way in, so this screen has exactly one job: send the browser
 * to it. QuoteHero never sees a password, which is the point of single sign-on and the
 * reason there is nothing on this page to phish.
 */
export function SignIn() {
  const { signIn, switchAccount, refusal, error } = useAuth()
  const [busy, setBusy] = useState(false)

  async function start() {
    setBusy(true)
    try {
      await signIn()
    } finally {
      // Only reached if the redirect never happened; on the happy path the page is gone.
      setBusy(false)
    }
  }

  async function changeAccount() {
    setBusy(true)
    try {
      await switchAccount()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Brand className="h-7" />

          <h1 className="mt-8 text-2xl font-semibold tracking-[-0.02em]">Sign in</h1>
          <p className="mt-1 text-[13px] text-muted">
            Quotations, approvals and the catalogue for every country you work in.
          </p>

          {/* Codemax authenticated them and QuoteHero will not have them. Saying so
              plainly matters: without it the only signal is a sign-in button that
              appears to do nothing, and they try it again all afternoon. */}
          {refusal && (
            <div
              role="alert"
              className="mt-7 rounded-md border border-warn/30 bg-warn-wash px-4 py-3"
            >
              <p className="text-[13px] font-medium text-warn">{refusal.title}</p>
              <p className="mt-1 text-[12px] text-ink-2">{refusal.detail ?? refusal.message}</p>
            </div>
          )}

          {error && (
            <div role="alert" className="mt-7 rounded-md border border-crit/30 bg-crit-wash px-4 py-3">
              <p className="text-[13px] font-medium text-crit">Cannot reach Codemax sign-in</p>
              <p className="mt-1 text-[12px] text-ink-2">{error}</p>
            </div>
          )}

          {/* Which button leads depends on what happened. A refusal means the Codemax
              account signing in is the wrong one - often a demo or a second account
              left signed in from another Codemax application - and pressing Continue
              would fetch the same token from the same ARMS cookie and be refused again.
              Changing account is the action that can actually succeed, so it leads. */}
          {refusal ? (
            <>
              <Button
                variant="primary"
                loading={busy}
                className="mt-7 w-full"
                onClick={() => void changeAccount()}
              >
                Sign in as a different Codemax account
              </Button>

              <Button
                variant="secondary"
                className="mt-3 w-full"
                onClick={() => window.location.assign('/')}
              >
                Try again
              </Button>

              <p className="mt-3 text-[12px] text-muted">
                Changing account signs you out of Codemax on this browser first, so every
                Codemax application will ask you to sign in again. Try again is for after
                an administrator has invited this address.
              </p>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                loading={busy}
                className="mt-7 w-full"
                onClick={() => void start()}
              >
                Continue with Codemax
              </Button>

              <p className="mt-3 text-[12px] text-muted">
                You will sign in at Codemax and come straight back. QuoteHero never sees your
                password.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Brand punctuation, and nothing else. No stock photography, no marketing copy -
          this is an internal tool and the people signing in already know what it does. */}
      <div className="brand-gradient hidden items-end p-10 lg:flex">
        <div className="max-w-md text-white">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] opacity-80">Codemax</p>
          <p className="mt-3 text-2xl font-semibold leading-snug">
            One quotation, one price, one approval chain - in every country.
          </p>
        </div>
      </div>
    </div>
  )
}
